<template>
	<div class="groups">
		<div class="flex-space-between--center" :class="{ flexJustifyEnd: !tableTitle }">
			<div class="table-title" v-if="tableTitle">{{ tableTitle }}</div>
			<div class="search-field">
				<AppSearch :searchString.sync="searchString" placeholder="Search groups" />
			</div>
		</div>
		<v-row no-gutters class="align-center">
			<v-col cols="12" class="mt-3">
				<v-simple-table class="users_table groups_table" fixed-header :loading="loadingGroups">
					<template v-slot:default>
						<thead>
							<tr>
								<th class="text-left">Group name</th>
								<th class="text-left">Sync</th>
								<th class="text-left">Sub groups</th>
								<th class="text-left">Users</th>
								<th style="width: 70px"></th>
							</tr>
						</thead>
						<tbody v-if="filteredGroups.length > 0">
							<tr
								v-for="group in filteredGroups"
								:key="getUserKey(group)"
								@mouseover="mouseOver(group)"
								@mouseleave="mouseLeave(group)"
								@click="goToGroup(group)"
								class="no-select group"
							>
								<td class="py-1">
									<GroupName :group="group" :showRenamePen="showRenamePen" :hasEditPermissions="hasEditPermissions" ref="name" />
								</td>
								<td>
									<div v-if="isSyncGroup(group)">
										<v-icon color="success" x-small v-if="isSyncActive(group)">mdi-check-circle</v-icon>
										<v-icon color="orange" x-small v-else>mdi-alert-circle</v-icon>
										{{ syncedGroupService(group) }}
									</div>
									<div v-else>-</div>
								</td>
								<td>{{ subGroupCount(group) }}</td>
								<td>
									{{ userCount(group) }}
								</td>
								<td>
									<div v-if="hasEditPermissions">
										<v-menu absolute right bottom :close-on-content-click="true">
											<template v-slot:activator="{ on, attrs }">
												<v-btn
													icon
													fab
													x-small
													rounded
													class="menu_btn"
													:class="{ show: hoverLine === getUserKey(group) }"
													:disabled="hoverLine !== getUserKey(group)"
													v-bind="attrs"
													v-on="on"
												>
													<SIcon name="dots-h" class="si" iconColor="#959DA1" style="top: -3px; left: -1px" :width="14"></SIcon>
												</v-btn>
											</template>
											<v-list>
												<v-list-item
													v-for="(item, index) in getMenuItems()"
													:key="`${getUserKey(group)}_${index}`"
													@click="menuAction(item, group)"
												>
													<v-list-item-title>{{ item.title }}</v-list-item-title>
												</v-list-item>
											</v-list>
										</v-menu>
									</div>
								</td>
							</tr>
						</tbody>
						<tr v-else-if="!loadingGroups">
							<span>No data</span>
						</tr>
					</template>
				</v-simple-table>
			</v-col>
		</v-row>
		<DeleteGroupDialog
			:groupName="dialogGroup.name"
			@ok="deleteGroup"
			@cancel="deleteGroupDialogShow = false"
			:showDialog.sync="deleteGroupDialogShow"
		/>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import DeleteGroupDialog from '../../dialogs/settings/groups/DeleteGroupDialog.vue';
import GroupName from '@/components/Settings/groups/GroupName';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { getGroups, createGroup, deleteGroup } from '@/store/modules/accountGroups/actions';
import { loading, defaultGroupName } from '@/store/modules/accountGroups/getters';
import { getService } from '@/store/modules/apps/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';

export default {
	name: 'GroupsList',
	components: {
		GroupName,
		DeleteGroupDialog,
	},
	props: {
		groups: { type: Array },
		tableTitle: { type: String },
		hasEditPermissions: { type: Boolean },
	},
	computed: {
		...mapGetters([loading.fullPath, defaultGroupName.fullPath, getService.fullPath]),
		...mapGettersShorthand({ hasAccountPermission }),
		allGroups() {
			return this.groups;
		},
		filteredGroups() {
			const allGroups = this.allGroups;
			if (this.searchString) {
				return allGroups.filter((u) => u.name.toLowerCase().includes(this.searchString.toLowerCase()));
			}
			return allGroups;
		},
		canManageGroups() {
			return this.hasAccountPermission(AccountPermissions.ManageGroups);
		},
		loadingGroups() {
			return this[loading.fullPath];
		},
		showRenamePen() {
			return false;
		},
	},
	methods: {
		...mapActions([getGroups.fullPath, createGroup.fullPath, deleteGroup.fullPath]),
		userCount(group) {
			return group?.all_users?.length || 0;
		},
		subGroupCount(group) {
			return group?.sub_groups?.length || '-';
		},
		syncedGroupService(group) {
			return this[getService.fullPath](group.synced?.service)?.service_name;
		},
		isSyncGroup(group) {
			return !!group.synced;
		},
		isSyncActive(group) {
			return group.synced?.is_active;
		},
		getMenuItems() {
			const arr = [];
			if (this.canManageGroups) {
				arr.push({ id: 1, title: 'Delete', action: this.deleteGroupDialog });
				arr.push({ id: 0, title: 'Rename', action: this.renameGroup });
			}
			return arr;
		},
		renameGroup(group) {
			this.$nextTick(() => {
				this.$refs.name.find((x) => x.groupName === group.name)?.renameGroup();
			});
		},
		deleteGroupDialog(group) {
			this.dialogGroup = group;
			this.deleteGroupDialogShow = true;
		},
		deleteGroup() {
			this.deleteGroupDialogShow = false;
			this[deleteGroup.fullPath]({ id: this.dialogGroup.id });
		},
		menuAction(item, index) {
			item.action(index);
		},
		getUserKey(group) {
			return group.id;
		},
		mouseOver(group) {
			this.hoverLine = this.getUserKey(group);
		},
		mouseLeave() {
			this.hoverLine = -1;
		},
		goToGroup(group) {
			const refGroup = this.$refs.name.find((x) => x.name === group.name);
			const canClick = !refGroup?.editNameFlag;
			if (group.name !== this[defaultGroupName.fullPath] && canClick) {
				this.$router.push({
					name: 'ManageSingleGroup',
					params: { groupId: group.id },
				});
			}
		},
	},
	async mounted() {
		try {
			await this[getGroups.fullPath]();
		} catch (err) {
			this.$toast.error('Failed getting all groups');
		}
	},
	data: () => ({
		hoverLine: -1,
		searchString: '',
		dialogGroup: { name: '' },
		deleteGroupDialogShow: false,
	}),
};
</script>

<style scoped lang="scss">
h4.reg {
	font-weight: 400 !important;
}
th {
	border-bottom: 1px solid #93a1bb !important;
	color: #354052 !important;
	font-weight: 600 !important;
	font-size: 12px !important;
	padding-bottom: 0px !important;
	line-height: 20px !important;
	height: 36px !important;
}
td {
	border-bottom: 1px solid #eff1f7 !important;
}
.menu_btn {
	border: 1px solid #959da1 !important;
	width: 25px;
	height: 25px;
	background: #fafcff;
	transition: all 0.2s;
	opacity: 0;
}
.menu_btn.show {
	opacity: 1;
}
.men_card {
	width: 210px;
	z-index: 5;
	border-radius: 2px !important;
	border-bottom-right-radius: 6px !important;
}
.group {
	cursor: pointer;
}
.flexJustifyEnd {
	justify-content: flex-end;
}
</style>
